import React, { useMemo } from 'react';

// City-specific view when a city is selected
const CityViewTable = ({ data, city }) => {
  // Group data by source and entity (store location, product, app)
  const cityData = useMemo(() => {
    const result = {
      locations: [],
      products: [],
      apps: []
    };
    
    // Filter data for this city and organize by type
    data.forEach(review => {
      if (review.city === city) {
        if (review.source === 'Google Reviews') {
          // Find or create location entity
          const locationName = `${city} Store ${Math.floor(Math.random() * 5) + 1}`;
          let location = result.locations.find(loc => loc.name === locationName);
          
          if (!location) {
            location = {
              name: locationName,
              total: 0,
              positive: 0,
              negative: 0,
              neutral: 0,
              address: `${Math.floor(Math.random() * 999) + 1} Main St, ${city}`
            };
            result.locations.push(location);
          }
          
          // Update location stats
          location.total += 1;
          if (review.sentiment === 'Positive') location.positive += 1;
          else if (review.sentiment === 'Negative') location.negative += 1;
          else location.neutral += 1;
        } 
        else if (review.source === 'Amazon') {
          // Find or create product entity
          const productTypes = [
            'Water Purifier - RO',
            'Water Purifier - UV',
            'Water Purifier - Gravity',
            'Water Filter - Pitcher',
            'Water Filter - Faucet'
          ];
          const productName = productTypes[review.id % productTypes.length];
          
          let product = result.products.find(p => p.name === productName);
          
          if (!product) {
            product = {
              name: productName,
              total: 0,
              positive: 0,
              negative: 0,
              neutral: 0
            };
            result.products.push(product);
          }
          
          // Update product stats
          product.total += 1;
          if (review.sentiment === 'Positive') product.positive += 1;
          else if (review.sentiment === 'Negative') product.negative += 1;
          else product.neutral += 1;
        }
        else if (review.source === 'App Store' || review.source === 'Play Store') {
          // Find or create app entity
          let app = result.apps.find(a => a.name === review.source);
          
          if (!app) {
            app = {
              name: review.source,
              total: 0,
              positive: 0,
              negative: 0,
              neutral: 0
            };
            result.apps.push(app);
          }
          
          // Update app stats
          app.total += 1;
          if (review.sentiment === 'Positive') app.positive += 1;
          else if (review.sentiment === 'Negative') app.negative += 1;
          else app.neutral += 1;
        }
      }
    });
    
    return result;
  }, [data, city]);
  
  // Calculate percentage
  const getPercentage = (part, total) => {
    return total > 0 ? Math.round((part / total) * 100) : 0;
  };
  
  // Get entity icon based on type
  const getEntityIcon = (type) => {
    if (type === 'location') {
      return (
        <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"></path>
        </svg>
      );
    } else if (type === 'product') {
      return (
        <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M19 5H5c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 12H5V7h14v10z"></path>
          <path d="M7 9h10v2H7z"></path>
          <path d="M7 12h7v2H7z"></path>
        </svg>
      );
    } else if (type === 'app') {
      return (
        <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M17.6 9.48l1.84-3.18c.16-.31.04-.69-.26-.85-.29-.15-.65-.06-.83.22l-1.88 3.24c-2.86-1.21-6.08-1.21-8.94 0L5.65 5.67c-.19-.29-.58-.38-.87-.2-.28.18-.37.54-.22.83L6.4 9.48C3.3 11.25 1.28 14.44 1 18h22c-.28-3.56-2.3-6.75-5.4-8.52zM7 15.25c-.69 0-1.25-.56-1.25-1.25s.56-1.25 1.25-1.25 1.25.56 1.25 1.25-.56 1.25-1.25 1.25zm10 0c-.69 0-1.25-.56-1.25-1.25s.56-1.25 1.25-1.25 1.25.56 1.25 1.25-.56 1.25-1.25 1.25z"></path>
        </svg>
      );
    }
    return null;
  };
  
  // Check if we have data to display
  const hasLocationData = cityData.locations.length > 0;
  const hasProductData = cityData.products.length > 0;
  const hasAppData = cityData.apps.length > 0;
  
  return (
    <div>
      <h2 className="text-lg font-medium text-gray-900 mb-4">
        Review Distribution for {city}
      </h2>
      
      {/* Table for all entities */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Total Reviews
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Positive
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Negative
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Neutral
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Sentiment
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {/* Store Locations */}
            {cityData.locations.map((location, index) => (
              <tr key={`location-${index}`} className="hover:bg-gray-50">
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 flex items-center">
                  <span className="mr-2">{getEntityIcon('location')}</span>
                  <span>{location.name}</span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                  Store Location
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 font-medium">
                  {location.total}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                  {location.positive} ({getPercentage(location.positive, location.total)}%)
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                  {location.negative} ({getPercentage(location.negative, location.total)}%)
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                  {location.neutral} ({getPercentage(location.neutral, location.total)}%)
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 w-40">
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-red-500 via-yellow-400 to-green-500" 
                         style={{ width: `${getPercentage(location.positive, location.total)}%` }}>
                    </div>
                  </div>
                </td>
              </tr>
            ))}
            
            {/* Products */}
            {cityData.products.map((product, index) => (
              <tr key={`product-${index}`} className="hover:bg-gray-50">
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 flex items-center">
                  <span className="mr-2">{getEntityIcon('product')}</span>
                  <span>{product.name}</span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                  Amazon Product
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 font-medium">
                  {product.total}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                  {product.positive} ({getPercentage(product.positive, product.total)}%)
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                  {product.negative} ({getPercentage(product.negative, product.total)}%)
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                  {product.neutral} ({getPercentage(product.neutral, product.total)}%)
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 w-40">
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-red-500 via-yellow-400 to-green-500" 
                         style={{ width: `${getPercentage(product.positive, product.total)}%` }}>
                    </div>
                  </div>
                </td>
              </tr>
            ))}
            
            {/* Apps */}
            {cityData.apps.map((app, index) => (
              <tr key={`app-${index}`} className="hover:bg-gray-50">
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 flex items-center">
                  <span className="mr-2">{getEntityIcon('app')}</span>
                  <span>{app.name}</span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                  Mobile App
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 font-medium">
                  {app.total}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                  {app.positive} ({getPercentage(app.positive, app.total)}%)
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                  {app.negative} ({getPercentage(app.negative, app.total)}%)
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                  {app.neutral} ({getPercentage(app.neutral, app.total)}%)
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 w-40">
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-red-500 via-yellow-400 to-green-500" 
                         style={{ width: `${getPercentage(app.positive, app.total)}%` }}>
                    </div>
                  </div>
                </td>
              </tr>
            ))}
            
            {/* No data message */}
            {!hasLocationData && !hasProductData && !hasAppData && (
              <tr>
                <td colSpan="7" className="px-4 py-8 text-center text-sm text-gray-500">
                  No data available for {city}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CityViewTable;